/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { type SearchResultDataTablePartial } from '@atlassian/search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';
import { useSelectedProduct } from '../../../../../../../controllers/store/search';

import { messages } from './messages';

interface DataTableSearchResultProps {
	partial: SearchResultDataTablePartial;
	/** The position in the results list shown to the user. */
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
}

export const DataTableSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
}: DataTableSearchResultProps) => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();
	const appMode = useApplicationMode();

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const { url, entity, title, lastModifiedDate, excerpt } = partial;
	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;

	const getOwnerName = () => {
		if (!entity?.owners || !Array.isArray(entity.owners) || entity.owners.length === 0) {
			return undefined;
		}
		return entity.owners[0]?.thirdPartyUser?.name;
	};

	const workspaceName = entity?.workspaceName;
	const ownerName = getOwnerName();

	const headerMetadataParts = [
		formatMessage(messages.eventLabel),
		['type', entity?.dataTableType],
		['workspaceName', workspaceName],
		...(workspaceName && ownerName && workspaceName === ownerName
			? []
			: ownerName
				? [['owner', ownerName]]
				: []),
		['last-modified', lastModifiedComponent],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			icon={icon}
			description={processExcerpt(excerpt)}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
		/>
	);
};
